import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { extractSubdomain, isApexHost } from './subdomain.util';

interface SubdomainConfigResponse {
  clientIdAudience: string;
}

@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private _httpClient = inject(HttpClient);

  clientId: string = environment.aryzaClientId;
  authUrl: string = environment.aryzaAuthUrl;

  /** True when this tenant can sign in with SSO: the subdomain lookup returned a client id
   *  (or a dev fallback clientId is preconfigured in the environment). */
  ssoEnabled = false;

  /** True when no subdomain could be determined (apex domain / www). */
  organisationUndetermined = false;

  /** The resolved tenant subdomain — the Front Door-injected `Subdomain` header when present,
   *  otherwise derived from the browser URL. */
  subdomain = '';

  async init(): Promise<void> {
    if (!environment.aryzaSSOEnabled) return;

    // Front Door injects a `Subdomain` header (based on host) on responses served through it.
    // Read it from a *same-origin* Front Door response — same-origin responses expose all headers
    // to JS (unlike the cross-origin API call). Fall back to deriving it from the browser URL.
    this.subdomain = (await this.readFrontDoorSubdomain()) || extractSubdomain(window.location.hostname);

    try {
      const result = await firstValueFrom(
        this._httpClient.get<SubdomainConfigResponse>(
          `${environment.apiRoot}/sso/client-by-subdomain?subdomain=${encodeURIComponent(this.subdomain)}`
        )
      );
      if (result?.clientIdAudience) {
        this.clientId = result.clientIdAudience;
        this.ssoEnabled = true;
      }
    } catch (err) {
      // 404: subdomain not registered for SSO (non-SSO tenant) — password login only.
      // 400: the backend couldn't determine a subdomain. Apex/www hosts are also
      // "organisation could not be determined" even when the backend answers 404
      // (it derives a lookup value from the Origin host's first label).
      if ((err instanceof HttpErrorResponse && err.status === 400) || isApexHost(window.location.hostname)) {
        this.organisationUndetermined = true;
      }
      // Dev/local fallback: an environment-supplied clientId keeps the SSO button usable
      // without a registered subdomain. Never blocks app startup.
      this.ssoEnabled = !!environment.aryzaClientId;
    }
  }

  /**
   * Fetch a same-origin, Front Door-served resource and read the injected `Subdomain` header.
   * Same-origin responses expose all headers to JS, so no `Access-Control-Expose-Headers` is needed.
   * Returns '' if the header is absent (e.g. local dev, or the service worker served a cached copy).
   */
  private async readFrontDoorSubdomain(): Promise<string> {
    try {
      const res = await fetch('/', { method: 'GET', cache: 'no-store' });
      return res.headers.get('Subdomain') ?? '';
    } catch {
      return '';
    }
  }
}
